/**
 * PII (Personally Identifiable Information) Sanitizer
 * 
 * Фильтрует персональные данные перед отправкой в системы аналитики
 * Защищает от случайной утечки чувствительной информации
 */

/** Паттерны для определения PII данных */
const PII_PATTERNS = {
  // Телефоны (разные форматы)
  phone: /(\+?7|8)?[\s-]?\(?\d{3}\)?[\s-]?\d{3}[\s-]?\d{2}[\s-]?\d{2}/gi,
  // Email
  email: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/gi,
  // ИИН/БИН Казахстан (12 цифр)
  iin: /\b\d{12}\b/g,
  // Номера карт (13-19 цифр)
  cardNumber: /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{1,7}\b/g,
  // IP адреса
  ip: /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/g,
}

/** Ключи, которые могут содержать PII */
const PII_KEYS = new Set([
  'phone',
  'telephone',
  'tel',
  'mobile',
  'email',
  'mail',
  'password',
  'passwd',
  'pwd',
  'secret',
  'token',
  'apikey',
  'api_key',
  'apiKey',
  'card',
  'cardNumber',
  'card_number',
  'cvv',
  'cvc',
  'ssn',
  'iin',
  'bin',
  'passport',
  'address',
  'fullName',
  'full_name',
  'firstName',
  'first_name',
  'lastName',
  'last_name',
  'middleName',
  'middle_name',
  // 'name' убран - используется для имени товара
  // Для персональных имен используйте firstName/lastName/fullName
  'customerName',
  'customer_name',
  'userName',
  'user_name',
  'ip',
  'ipAddress',
  'ip_address',
])

/** Ключи, которые безопасны и не должны фильтроваться */
const SAFE_KEYS = new Set([
  // Ecommerce
  'name', // Имя товара - безопасно
  'item_name',
  'item_id',
  'item_brand',
  'item_category',
  'item_variant',
  'item_list_name',
  'item_list_id',
  'product_name',
  'product_id',
  'brand',
  'category',
  'listName',
  'listId',
  'sku',
  'variant',
  // События
  'event',
  'eventName',
  'event_name',
  'page_title',
  'page_path',
  'page_location',
  'currency',
  'value',
  'price',
  'quantity',
  'discount',
  'coupon',
  'transaction_id',
  'transactionId',
  'shipping',
  'tax',
  'method',
  'search_term',
  'searchTerm',
  'shippingTier',
  'shipping_tier',
  'paymentType',
  'payment_type',
])

/**
 * Проверить, является ли ключ потенциально опасным для PII
 */
function isPIIKey (key: string): boolean {
  const normalizedKey = key.toLowerCase()
  
  // Сначала проверяем безопасные ключи
  if (SAFE_KEYS.has(key) || SAFE_KEYS.has(normalizedKey)) {
    return false
  }
  
  // Проверяем опасные ключи
  return PII_KEYS.has(key) || PII_KEYS.has(normalizedKey)
}

/**
 * Маскировать строковое значение
 */
function maskString (value: string): string {
  if (value.length <= 4) {
    return '***'
  }
  return value.slice(0, 2) + '***' + value.slice(-2)
}

/**
 * Проверить и маскировать PII паттерны в строке
 */
function sanitizeString (value: string): string {
  let result = value
  
  // Маскируем телефоны
  result = result.replace(PII_PATTERNS.phone, '[PHONE]')
  
  // Маскируем email
  result = result.replace(PII_PATTERNS.email, '[EMAIL]')
  
  // Маскируем ИИН
  result = result.replace(PII_PATTERNS.iin, '[IIN]')
  
  // Маскируем номера карт
  result = result.replace(PII_PATTERNS.cardNumber, '[CARD]')
  
  return result
}

/**
 * Рекурсивно санитизировать объект
 */
function sanitizeObject (
  obj: Record<string, unknown>,
  depth: number = 0
): Record<string, unknown> {
  // Защита от слишком глубокой рекурсии
  if (depth > 10) {
    return obj
  }
  
  const result: Record<string, unknown> = {}
  
  for (const [ key, value ] of Object.entries(obj)) {
    // Проверяем, является ли ключ PII
    if (isPIIKey(key)) {
      // Маскируем значение
      if (typeof value === 'string') {
        result[key] = maskString(value)
      }
      else {
        result[key] = '[REDACTED]'
      }
      continue
    }
    
    // Обрабатываем значение в зависимости от типа
    if (value === null || value === undefined) {
      result[key] = value
    }
    else if (typeof value === 'string') {
      result[key] = sanitizeString(value)
    }
    else if (typeof value === 'number' || typeof value === 'boolean') {
      result[key] = value
    }
    else if (Array.isArray(value)) {
      result[key] = value.map((item) => {
        if (typeof item === 'object' && item !== null) {
          return sanitizeObject(item as Record<string, unknown>, depth + 1)
        }
        if (typeof item === 'string') {
          return sanitizeString(item)
        }
        return item
      })
    }
    else if (typeof value === 'object') {
      result[key] = sanitizeObject(value as Record<string, unknown>, depth + 1)
    }
    else {
      result[key] = value
    }
  }
  
  return result
}

/**
 * Санитизировать данные перед отправкой в аналитику
 * 
 * @param data - Данные для санитизации
 * @returns Санитизированные данные без PII
 * 
 * @example
 * ```ts
 * const safe = sanitizePII({
 *   phone: '+77001234567',
 *   item_name: 'Молоко',
 *   email: 'test@example.com'
 * })
 * // { phone: '+7***67', item_name: 'Молоко', email: 'te***om' }
 * ```
 */
export function sanitizePII<T extends Record<string, unknown>> (data: T): T {
  try {
    return sanitizeObject(data) as T
  }
  catch (error) {
    console.error('[PII Sanitizer] Error:', error)
    // В случае ошибки возвращаем пустой объект для безопасности
    return {} as T
  }
}

/**
 * Проверить, содержит ли объект PII данные
 * Полезно для логирования и отладки
 */
export function containsPII (data: Record<string, unknown>): boolean {
  const checkValue = (value: unknown): boolean => {
    if (typeof value === 'string') {
      return (
        PII_PATTERNS.phone.test(value) ||
        PII_PATTERNS.email.test(value) ||
        PII_PATTERNS.cardNumber.test(value)
      )
    }
    if (Array.isArray(value)) {
      return value.some(checkValue)
    }
    if (typeof value === 'object' && value !== null) {
      return Object.values(value).some(checkValue)
    }
    return false
  }
  
  // Проверяем ключи
  for (const key of Object.keys(data)) {
    if (isPIIKey(key)) {
      return true
    }
  }
  
  // Проверяем значения
  return Object.values(data).some(checkValue)
}

export default sanitizePII
